import { Dimmer, Grid, Loader, Modal } from 'semantic-ui-react';
import {
  ReasonForEncounterDto,
  ReasonForEncounterSearchRequest,
  UpsertReasonForEncounterDto
} from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { debounce } from 'ts-debounce';
import { FormState } from 'final-form';
import VirtualizedTable from "components/layout/VirtualizedTable";
import {
  deleteReasonForEncounter,
  findReasonForEncounterById,
  searchReasonForEncounter
} from 'services/reasonForEncounterServices';
import UpsertReasonsForEncounterForm from 'components/settings/reason-for-encounter/UpsertReasonsForEncounterForm';
import { noop } from 'utils/functionUtils';
import DeleteActionButton from 'components/DeleteActionButton';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SearchResultComponent from 'components/SearchResultComponent';

const initialReasonForEncounterSearch: Partial<ReasonForEncounterSearchRequest> = {};

interface ReasonForEncounterFieldProps extends FormState {
  values: Partial<ReasonForEncounterSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundReasonForEncounter: Array<ReasonForEncounterDto>,
  reasonForEncounterLoaded: boolean,
  reasonForEncounter?: UpsertReasonForEncounterDto,
  reasonForEncounterId?: number,
  addEditModalOpen: boolean,
  loading: boolean,
  searchValue: Partial<ReasonForEncounterSearchRequest>,
  cancelTokenSource: CancelTokenSource,
}

class ReasonForEncounterView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundReasonForEncounter: [],
      reasonForEncounterLoaded: false,
      reasonForEncounter: undefined,
      reasonForEncounterId: undefined,
      addEditModalOpen: false,
      loading: false,
      searchValue: initialReasonForEncounterSearch,
      cancelTokenSource
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by User');
  }

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findReasonForEncounterById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: number, reasonForEncounter?: UpsertReasonForEncounterDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        reasonForEncounter: reasonForEncounter,
        reasonForEncounterId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      reasonForEncounterId: undefined,
      reasonForEncounter: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValue, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        reasonForEncounterLoaded: true
      })
    };

    searchReasonForEncounter(searchValue, cancelTokenSource)
        .then(response => {
          this.setState({
            foundReasonForEncounter: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  deleteReasonForEncounter = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteReasonForEncounter(id, cancelTokenSource).then(response => {
      this.setState(({foundReasonForEncounter}: State) => ({
        foundReasonForEncounter: foundReasonForEncounter.filter((reasonForEncounter: ReasonForEncounterDto) => reasonForEncounter.id != response.id)
      }))
    }).catch(noop);
  };

  handleReasonForEncounterChange = debounce(({values}: ReasonForEncounterFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;
    this.setState({reasonForEncounterLoaded: false});

    const onFinally = () => {
      this.setState({
        reasonForEncounterLoaded: true
      })
    };

    searchReasonForEncounter(values, cancelTokenSource)
        .then(response =>
            this.setState({
              foundReasonForEncounter: response,
              searchValue: values
            })
        )
        .catch(noop)
        .finally(onFinally);
  }, 300);

  render(): React.ReactNode {
    const {reasonForEncounterLoaded} = this.state;
    const reasonForEncounterView = (
        <PraxStyledSegment>

          <div>
            {this.renderSearchReasonForEncounterSegment()}
          </div>

          <StyledViewTableRow>
            {!reasonForEncounterLoaded ? this.renderLoader() : this.renderReasonForEncounterSegment()}
          </StyledViewTableRow>

          {this.renderUpsertReasonForEncounterModal()}
        </PraxStyledSegment>
    );
    return (
        <React.Fragment>
          {reasonForEncounterView}
        </React.Fragment>
    );
  }

  renderSearchReasonForEncounterSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{}}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundReasonForEncounter} = this.state;

    const numResults = foundReasonForEncounter.length;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="search"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleReasonForEncounterChange}/>
        </PxGrid>
    );
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("reasonForEncounter.confirmDelete")}
              onConfirm={() => this.deleteReasonForEncounter(cellData)}
          />
        </React.Fragment>
    );
  };

  reasonForEncounterRowGetter = ({index}: any) => {
    const {foundReasonForEncounter} = this.state;

    Object.assign(foundReasonForEncounter[index], {index: index + 1});

    return foundReasonForEncounter[index];
  };

  foundReasonForEncounterRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundReasonForEncounter} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundReasonForEncounter[index].id)}
        >
          {columns}
        </div>
    );
  };

  renderReasonForEncounterSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundReasonForEncounter} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundReasonForEncounter.length}
            rowGetter={this.reasonForEncounterRowGetter}
            rowRenderer={this.foundReasonForEncounterRowRenderer}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 200,
                label: (getMessage("reasonForEncounter.code")),
                dataKey: "code"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("reasonForEncounter.description")),
                dataKey: "description"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("reasonForEncounter.loader")}</Loader>
        </Dimmer>
    );
  };

  renderUpsertReasonForEncounterModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, reasonForEncounter, reasonForEncounterId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>
            {getMessage("reasonForEncounter")}
          </Modal.Header>
          <FormModalContentContainer>
            <UpsertReasonsForEncounterForm
                reasonForEncounter={reasonForEncounter}
                reasonForEncounterId={reasonForEncounterId}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };
}

export default withMessages(ReasonForEncounterView);
